// src/features/calendar/utils/timeBoxCreation.ts
// TimeBox 建立、同步、Task 更新的純函數
// 從 Calendar.tsx handleConfirmPlacement 提取

import { TimeBox } from '../../../types/timeBox';
import { CreateTimeBoxRequest } from '../../../types/timeBox';
import { Task } from '../../../types/task';
import { getCurrentUserId } from '../../../services/auth';
import { getTimeBoxCategory } from '../../../config/taskStyles';
import { createTimeBoxLocal, replaceTimeBoxTempId, updateTaskLocal, getTaskById, getTimeBoxById } from '../../../services/indexedDB';
import { createTimeBox } from '../../../services/api/timeBoxes';
import { taskService } from '../../task';
import { addToSyncQueue } from '../../../services/syncQueueService';
import { logger } from '../../../utils/logger';
import { format } from 'date-fns';

/**
 * 計算 Task 關聯的所有 TimeBox 中最大的日期（YYYY-MM-DD）
 * 從 IndexedDB 查詢各 TimeBox 的 startTime，取最大值。
 * 若所有 ID 都查不到（TimeBox 已刪），回傳 undefined。
 */
export async function computeLastTimeboxedDate(scheduledTimeBoxIds: string[]): Promise<string | undefined> {
  if (!scheduledTimeBoxIds.length) return undefined;

  const dates: string[] = [];
  for (const id of scheduledTimeBoxIds) {
    const timeBox = await getTimeBoxById(id);
    if (timeBox) {
      dates.push(format(timeBox.startTime, 'yyyy-MM-dd'));
    }
  }

  if (!dates.length) return undefined;
  return dates.reduce((max, date) => (date > max ? date : max));
}

/**
 * 建構 CreateTimeBoxRequest 物件
 * 共用於 handleConfirmPlacement、handleCreateTimeBoxFromTask、handleQuickScheduleTask
 */
export function buildCreateTimeBoxRequest(
  task: Task,
  startTime: Date,
  endTime: Date,
  userId: string
): CreateTimeBoxRequest {
  return {
    itemType: 'timebox',
    userId,
    title: task.title,
    startTime,
    endTime,
    category: getTimeBoxCategory(task.category),
    taskIds: [task.id],
    description: task.notes || '',
    location: task.location || '',
    notes: '',
    status: 'normal',
  };
}

/**
 * 建構本地 TimeBox 物件並寫入 IndexedDB
 * 用於放置模式的樂觀更新
 */
export async function buildAndSaveLocalTimeBox(
  task: Task,
  startTime: Date,
  endTime: Date,
): Promise<TimeBox> {
  const tempId = `temp-${Date.now()}`;
  const userId = getCurrentUserId();
  const now = new Date();

  const localTimeBox: TimeBox = {
    id: tempId,
    itemType: 'timebox',
    userId,
    title: task.title,
    startTime,
    endTime,
    category: getTimeBoxCategory(task.category),
    taskIds: [task.id],
    status: 'normal',
    description: task.notes || '',
    location: task.location || '',
    notes: '',
    createdAt: now,
  };

  await createTimeBoxLocal(localTimeBox);
  logger.info('Calendar', 'TimeBox saved to IndexedDB', { timeBoxId: tempId });

  return localTimeBox;
}

/**
 * 更新 Task 的 scheduledTimeBoxIds（新增 timeBoxId）並同步 lastTimeboxedDate（IndexedDB）
 * @returns 更新後的 lastTimeboxedDate（供 Firestore 同步使用）
 */
export async function addTimeBoxIdToTask(
  taskId: string,
  timeBoxId: string,
  timeBoxDate: string,
): Promise<string | undefined> {
  try {
    const cachedTask = await getTaskById(taskId);
    if (!cachedTask) return undefined;

    const currentScheduledIds = cachedTask.scheduledTimeBoxIds || [];
    const currentLast = cachedTask.lastTimeboxedDate;
    const newLastTimeboxedDate = !currentLast || timeBoxDate > currentLast ? timeBoxDate : currentLast;

    await updateTaskLocal(taskId, {
      scheduledTimeBoxIds: [...currentScheduledIds, timeBoxId],
      lastTimeboxedDate: newLastTimeboxedDate,
      updatedAt: new Date(),
    });
    logger.info('Calendar', 'Task marked as scheduled', { taskId, timeBoxId, lastTimeboxedDate: newLastTimeboxedDate });
    return newLastTimeboxedDate;
  } catch (error) {
    logger.warn('Calendar', 'Task local update failed (TimeBox creation unaffected)', error);
    return undefined;
  }
}

/**
 * 同步 TimeBox 到 Firestore 並替換臨時 ID
 * 失敗時加入同步佇列
 *
 * @param lastTimeboxedDate 由呼叫方從 addTimeBoxIdToTask 取得後直接傳入，
 *   避免從 IndexedDB 重新讀取時遭 onSnapshot 覆蓋的競爭條件。
 */
export async function syncTimeBoxToFirestore(
  localTimeBox: TimeBox,
  task: Task,
  lastTimeboxedDate?: string,
): Promise<void> {
  const isOnline = navigator.onLine;

  if (!isOnline) {
    logger.info('Calendar', 'Offline: TimeBox added to sync queue', {
      timeBoxId: localTimeBox.id,
    });
    await addToSyncQueue('CREATE_TIMEBOX', localTimeBox.id, localTimeBox);
    return;
  }

  try {
    const request = buildCreateTimeBoxRequest(
      task,
      localTimeBox.startTime,
      localTimeBox.endTime,
      localTimeBox.userId,
    );
    const result = await createTimeBox(request);

    // 替換臨時 ID 為真實 ID
    await replaceTimeBoxTempId(localTimeBox.id, result.id);
    logger.info('Calendar', 'TimeBox synced to Firestore', {
      tempId: localTimeBox.id,
      realId: result.id,
    });

    // 更新 Task 的 scheduledTimeBoxIds（替換臨時 ID）
    await replaceTimeBoxIdInTask(task.id, localTimeBox.id, result.id);

    // 同步 lastTimeboxedDate 到 Firestore（使用呼叫方傳入的值，避免競爭條件）
    if (lastTimeboxedDate) {
      await taskService.updateLastTimeboxedDate(task.id, lastTimeboxedDate);
    }
  } catch (syncError) {
    logger.warn('Calendar', 'TimeBox Firestore sync failed, adding to sync queue', {
      timeBoxId: localTimeBox.id,
      error: syncError,
    });
    await addToSyncQueue('CREATE_TIMEBOX', localTimeBox.id, localTimeBox);
  }
}

/**
 * 替換 Task 的 scheduledTimeBoxIds 中的臨時 ID
 */
async function replaceTimeBoxIdInTask(
  taskId: string,
  oldTimeBoxId: string,
  newTimeBoxId: string,
): Promise<void> {
  try {
    const cachedTask = await getTaskById(taskId);
    if (!cachedTask?.scheduledTimeBoxIds) return;

    const updatedIds = cachedTask.scheduledTimeBoxIds.map(id =>
      id === oldTimeBoxId ? newTimeBoxId : id
    );
    await updateTaskLocal(taskId, {
      scheduledTimeBoxIds: updatedIds,
      updatedAt: new Date(),
    });
    logger.info('Calendar', 'Task TimeBox ID replaced', {
      taskId,
      oldTimeBoxId,
      newTimeBoxId,
    });
  } catch (error) {
    logger.warn('Calendar', 'Task ID replacement failed (sync unaffected)', error);
  }
}
